import { supabase, HistorialTrasladoMiembro, HistorialTrasladoMiembroResuelto } from '../lib/supabase';

export interface TrasladoMiembroInput {
  readonly miembro_id: string;
  readonly lumisial_destino_id: string;
  readonly motivo?: string;
}

export const trasladoMiembroService = {
  async registrarTraslado(input: TrasladoMiembroInput): Promise<HistorialTrasladoMiembro> {
    const { data, error } = await supabase.rpc('trasladar_miembro', {
      p_miembro_id: input.miembro_id,
      p_lumisial_destino_id: input.lumisial_destino_id,
      p_motivo: input.motivo?.trim() ?? '',
    });
    if (error) throw error;
    if (!data) throw new Error('El traslado no devolvió su historial');
    return data as HistorialTrasladoMiembro;
  },

  async obtenerHistorialPorMiembro(miembroId: string): Promise<HistorialTrasladoMiembroResuelto[]> {
    // RPC en vez de select con joins: los joins a `lumisiales`/`diocesis`/`provincias_eclesiasticas`
    // quedan sujetos a la RLS de jurisdicción de esas tablas, y para un rol `*_local` el extremo
    // ajeno del traslado llega vacío (bloqueo B3 del UAT). La función resuelve los nombres en el
    // servidor. Autoriza fila por fila con el mismo predicado que la política SELECT del historial
    // (cada `admin_*` ve las filas cuyo origen o destino toca su ámbito; cualquiera ve el propio):
    // NO con "quien pueda ver la ficha", que fue el diseño descartado por ampliar el acceso.
    const { data, error } = await supabase.rpc('obtener_historial_traslados_miembro', {
      p_miembro_id: miembroId,
    });

    if (error) throw error;
    return data || [];
  },

  async obtenerHistorialPorLumisial(lumisialId: string): Promise<HistorialTrasladoMiembro[]> {
    const { data, error } = await supabase
      .from('historial_traslados_miembros')
      .select(`
        *,
        provincia_eclesiastica_origen:provincias_eclesiasticas!provincia_eclesiastica_origen_id(*),
        diocesis_origen:diocesis!diocesis_origen_id(*),
        lumisial_origen:lumisiales!lumisial_origen_id(*),
        provincia_eclesiastica_destino:provincias_eclesiasticas!provincia_eclesiastica_destino_id(*),
        diocesis_destino:diocesis!diocesis_destino_id(*),
        lumisial_destino:lumisiales!lumisial_destino_id(*)
      `)
      .or(`lumisial_origen_id.eq.${lumisialId},lumisial_destino_id.eq.${lumisialId}`)
      .order('fecha_traslado', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async obtenerHistorialReciente(limit: number = 50): Promise<HistorialTrasladoMiembro[]> {
    const { data, error } = await supabase
      .from('historial_traslados_miembros')
      .select(`
        *,
        provincia_eclesiastica_origen:provincias_eclesiasticas!provincia_eclesiastica_origen_id(*),
        diocesis_origen:diocesis!diocesis_origen_id(*),
        lumisial_origen:lumisiales!lumisial_origen_id(*),
        provincia_eclesiastica_destino:provincias_eclesiasticas!provincia_eclesiastica_destino_id(*),
        diocesis_destino:diocesis!diocesis_destino_id(*),
        lumisial_destino:lumisiales!lumisial_destino_id(*)
      `)
      .order('fecha_traslado', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async contarTrasladosPorMiembro(miembroId: string): Promise<number> {
    const { count, error } = await supabase
      .from('historial_traslados_miembros')
      .select('*', { count: 'exact', head: true })
      .eq('miembro_id', miembroId);

    if (error) throw error;
    return count || 0;
  },

  async validarTraslado(lumisialOrigenId: string, lumisialDestinoId: string): Promise<{
    valido: boolean;
    mensaje?: string;
  }> {
    // Solo la comprobación que el cliente puede hacer con certeza. Antes leía `lumisiales` para
    // confirmar que el destino estuviera activo, pero esa lectura pasa por la política de
    // jurisdicción: una Junta Sacerdotal local no ve los demás Lumisiales y recibía un falso
    // "no está activo" (bloqueo B2 del UAT). Que el destino exista y esté activo lo verifica
    // `trasladar_miembro` en el servidor, que es donde además se autoriza el traslado.
    if (lumisialOrigenId === lumisialDestinoId) {
      return { valido: false, mensaje: 'El Lumisial de destino debe ser distinto del actual' };
    }
    return { valido: true };
  }
};
